import { useState, useRef, useMemo } from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  ScrollArea,
  cn,
} from '@design-system';

import { Check, ChevronsUpDown } from 'lucide-react';
import { useDocusignTemplates } from '@/features/quote-and-apply-builder/hooks/use-docusign-templates';

interface TemplateNameComboboxProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  id?: string;
}

export function TemplateNameCombobox({
  value,
  onChange,
  placeholder = 'Select a DocuSign template...',
  className,
  id,
}: TemplateNameComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, error } = useDocusignTemplates();
  const templates = data?.templates ?? [];

  const filteredTemplates = useMemo(() => {
    if (!search) return templates;
    const lower = search.toLowerCase();
    return templates.filter(template => template.name.toLowerCase().includes(lower));
  }, [templates, search]);

  const valueExistsInOptions = templates.some(template => template.name === value);

  return (
    <>
      <Button
        id={id}
        type="button"
        variant="outline"
        role="combobox"
        aria-expanded={open}
        className={cn('w-full justify-between font-normal', className)}
        onClick={() => setOpen(true)}
      >
        <span className="truncate">{value || placeholder}</span>
        <ChevronsUpDown className="shrink-0 opacity-50" />
      </Button>

      {value && !valueExistsInOptions && !isLoading && (
        <p className="text-destructive text-xs">
          Current value &quot;{value}&quot; does not match any active DocuSign template. Choose a
          template from the list.
        </p>
      )}

      <Dialog
        open={open}
        onOpenChange={isOpen => {
          setOpen(isOpen);
          if (!isOpen) setSearch('');
        }}
      >
        <DialogContent className="gap-0 p-0">
          <DialogHeader className="border-b p-4">
            <DialogTitle>Select a DocuSign Template</DialogTitle>
          </DialogHeader>
          <div className="border-b">
            <Input
              ref={inputRef}
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Search templates..."
              autoFocus
              className="border-0 px-4 focus-visible:border-transparent focus-visible:ring-0"
            />
          </div>
          <ScrollArea className="max-h-[60vh] min-w-0 [&>[data-slot=scroll-area-viewport]>div]:!block">
            <div className="p-2">
              {isLoading && (
                <p className="text-muted-foreground p-4 text-center text-sm">
                  Loading templates...
                </p>
              )}
              {error && (
                <p className="text-destructive p-4 text-center text-sm">
                  Failed to load DocuSign templates.
                </p>
              )}
              {!isLoading && !error && filteredTemplates.length === 0 && (
                <p className="text-muted-foreground p-4 text-center text-sm">No templates found.</p>
              )}
              {!isLoading &&
                !error &&
                filteredTemplates.map(template => (
                  <button
                    key={template.templateId}
                    type="button"
                    className={cn(
                      'hover:bg-accent my-0.5 flex w-full cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm',
                      value === template.name && 'bg-accent',
                    )}
                    onClick={() => {
                      onChange(template.name);
                      setOpen(false);
                      setSearch('');
                    }}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-medium">{template.name}</div>
                      {template.description && (
                        <div className="text-muted-foreground wrap-break-word text-xs">
                          {template.description}
                        </div>
                      )}
                    </div>
                    <Check
                      className={cn(
                        'mt-0.5 size-4 shrink-0 self-start',
                        value === template.name ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                  </button>
                ))}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
}
